import { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { blogApi, type BlogPostSummary } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useHorizontalRail } from "./rail/useHorizontalRail";
import {
    RailArrows,
    RailEdgeFades,
    RailEyebrow,
    RailPagination,
    RailProgressBar,
    RailScrollbarHideStyle,
    railContainerClass,
} from "./rail/RailControls";

const HOME_BLOG_LIMIT = 4;
const BLOG_ACCENT_HUES = [260, 190, 320, 160];

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso?: string) => {
        if (!iso) return "";
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const BlogCard = ({
    post,
    index,
    accentHue,
    staggerInView,
}: {
    post: BlogPostSummary;
    index: number;
    accentHue: number;
    staggerInView: boolean;
}) => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const formatDate = useFormatDate();
    const categoryName = post.category?.name ?? t("blog_home.default_category");
    const accent = `hsl(${accentHue} 90% 60%)`;
    const accentDim = `hsl(${accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${accentHue} 90% 60% / 0.18)`;

    return (
        <motion.article
            className="group relative h-full"
            initial={{ opacity: 0, y: 30 }}
            animate={staggerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
                duration: 0.55,
                delay: 0.08 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -4 }}
        >
            <div
                aria-hidden
                className="absolute -inset-px rounded-[28px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-10"
                style={{
                    background: `conic-gradient(from 140deg at 50% 50%, ${accent}, transparent 35%, transparent 65%, ${accent})`,
                    padding: "1px",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                }}
            />

            <Link
                to={localizedPath(`/blog/${post.slug}`)}
                className="glass-v2 rounded-[28px] overflow-hidden relative flex flex-col h-full"
                data-cursor="hover"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(600px circle at 50% 0%, ${accentSoft}, transparent 60%)`,
                    }}
                />

                <div className="relative w-full aspect-[16/10] overflow-hidden">
                    {post.featuredImage ? (
                        <img
                            src={post.featuredImage}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover:scale-[1.06]"
                        />
                    ) : (
                        <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                                background: `linear-gradient(135deg, ${accentSoft}, hsl(${accentHue} 90% 50% / 0.08))`,
                            }}
                        >
                            <span className="font-v2-display text-6xl text-white/20 select-none">
                                م
                            </span>
                        </div>
                    )}
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/70"
                    />
                    <span
                        aria-hidden
                        className="absolute top-3 end-3 font-v2-label text-[9px] px-2 py-0.5 rounded-full bg-background/60 backdrop-blur-md border border-white/10 text-white/70 tabular-nums"
                    >
                        {String(index + 1).padStart(2, "0")}
                    </span>
                </div>

                <div className="flex-1 p-6 md:p-7 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                        <span
                            className="font-v2-label text-[10px] px-3 py-1 rounded-full border"
                            style={{
                                background: accentSoft,
                                borderColor: accentDim,
                                color: `hsl(${accentHue} 90% 85%)`,
                            }}
                        >
                            {categoryName}
                        </span>
                        <span className="inline-flex items-center gap-1 font-v2-label text-[10px] text-white/40">
                            <Clock className="w-3 h-3" />
                            {post.readTime} {t("blog_home.read_time_suffix")}
                        </span>
                    </div>

                    <h3 className="font-v2-display text-lg md:text-xl text-white mb-3 leading-snug line-clamp-2 transition-colors duration-300 group-hover:text-white/90">
                        {post.title}
                    </h3>

                    {post.excerpt && (
                        <p className="text-sm text-muted-foreground leading-relaxed font-medium line-clamp-3 mb-4 flex-1">
                            {post.excerpt}
                        </p>
                    )}

                    <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                            {post.author?.avatar ? (
                                <img
                                    src={post.author.avatar}
                                    alt=""
                                    loading="lazy"
                                    className="w-7 h-7 rounded-full object-cover border border-white/10 shrink-0"
                                />
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                                    <span className="text-[10px] text-white/60">
                                        {post.author?.name?.[0] ?? "م"}
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs text-white/70 font-bold truncate leading-tight">
                                    {post.author?.name ?? t("blog_home.default_author")}
                                </span>
                                <span className="font-v2-label text-[9px] text-white/40 leading-tight mt-0.5">
                                    {formatDate(post.publishedAt)}
                                </span>
                            </div>
                        </div>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-45 shrink-0"
                            style={{
                                background: accentSoft,
                                border: `1px solid ${accentDim}`,
                            }}
                        >
                            <ArrowUpRight size={14} />
                        </div>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
};

const BlogSkeleton = () => (
    <div className="glass-v2 rounded-[28px] overflow-hidden flex flex-col animate-pulse h-full">
        <div className="w-full aspect-[16/10] bg-white/5" />
        <div className="flex-1 p-6 flex flex-col gap-3">
            <div className="flex gap-2">
                <div className="h-5 w-16 rounded-full bg-white/5" />
                <div className="h-5 w-12 rounded-full bg-white/5" />
            </div>
            <div className="h-4 w-4/5 bg-white/5 rounded" />
            <div className="h-4 w-3/5 bg-white/5 rounded" />
            <div className="h-3 w-full bg-white/5 rounded mt-2" />
            <div className="h-3 w-5/6 bg-white/5 rounded" />
            <div className="mt-auto h-3 w-1/3 bg-white/5 rounded" />
        </div>
    </div>
);

const BlogTimeline = () => {
    const { t } = useTranslation();
    const { lang, dir } = useLanguage();
    const isRtl = dir === "rtl";
    const localizedPath = useLocalizedPath();
    const sectionRef = useRef<HTMLElement>(null);
    const staggerInView = useInView(sectionRef, { amount: 0.15, margin: "-40px" });

    const { data, isLoading, isError } = useQuery({
        queryKey: ["home-blog", HOME_BLOG_LIMIT, lang],
        queryFn: async () => {
            const res = await blogApi.getPosts({ limit: HOME_BLOG_LIMIT, page: 1, lang });
            return res.data.data;
        },
        staleTime: 15 * 60 * 1000,
        retry: 1,
    });

    const featured = data?.featuredPost ?? data?.posts?.[0];
    const rest = (data?.posts ?? []).filter((p) => p.id !== featured?.id);
    const posts: BlogPostSummary[] = featured
        ? [featured, ...rest].slice(0, HOME_BLOG_LIMIT)
        : (data?.posts ?? []).slice(0, HOME_BLOG_LIMIT);

    const items: (BlogPostSummary | undefined)[] = isLoading
        ? Array.from({ length: HOME_BLOG_LIMIT })
        : [...posts, ...Array.from({ length: Math.max(0, HOME_BLOG_LIMIT - posts.length) })];

    const {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    } = useHorizontalRail({
        itemCount: items.length,
        cardSelector: "[data-blog-card]",
        isRtl,
    });

    if (!isLoading && (isError || posts.length === 0)) {
        return null;
    }

    return (
        <section ref={sectionRef} className="py-20 md:py-32 relative" id="blog">
            <div className="container mx-auto px-6 relative">
                <motion.div
                    className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <RailEyebrow>{t("blog_home.eyebrow")}</RailEyebrow>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("blog_home.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("blog_home.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("blog_home.subtitle")}
                    </p>
                </motion.div>

                <RailPagination
                    itemCount={items.length}
                    activeIndex={activeIndex}
                    onSelect={scrollToIndex}
                    accentHues={BLOG_ACCENT_HUES}
                    ariaLabelPrefix="article"
                />
            </div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.15, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
                {needsScroll && (
                    <>
                        <RailEdgeFades isRtl={isRtl} />
                        <RailArrows
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                            canPrev={canPrev}
                            canNext={canNext}
                            isRtl={isRtl}
                        />
                    </>
                )}

                <div
                    ref={railRef}
                    tabIndex={needsScroll ? 0 : -1}
                    aria-label="Articles carousel"
                    className={railContainerClass(needsScroll)}
                >
                    {items.map((post, i) => (
                        <div
                            key={post?.id ?? i}
                            data-blog-card
                            className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[300px] xl:w-[285px] 2xl:w-[330px]"
                        >
                            {isLoading || !post ? (
                                <BlogSkeleton />
                            ) : (
                                <BlogCard
                                    post={post}
                                    index={i}
                                    accentHue={BLOG_ACCENT_HUES[i % BLOG_ACCENT_HUES.length]}
                                    staggerInView={staggerInView}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {needsScroll && <RailProgressBar progress={scrollProgress} />}
            </motion.div>

            <div className="container mx-auto px-6">
                <div className="mt-8 md:mt-12 flex items-center justify-center">
                    <Link to={localizedPath("/blog")} data-cursor="hover">
                        <Button
                            className="rounded-full text-white font-bold gap-2 h-11 border-0"
                            style={{
                                background: "var(--gradient-accent-v2)",
                                boxShadow: "0 10px 28px -10px hsl(260 100% 55% / 0.5)",
                            }}
                        >
                            {t("blog_home.see_all")}
                            <ArrowLeft size={16} />
                        </Button>
                    </Link>
                </div>
            </div>

            <RailScrollbarHideStyle />
        </section>
    );
};

export default BlogTimeline;
